export const TREE_VIEW_STATE_SCHEMA_VERSION = 1;
export const TREE_DATA_SNAPSHOT_VERSION = 1;
export const TREE_LABEL_VIEWPORT_MARGIN = 120;

export function getTreeLabelBudget(scale) {
    const zoom = Number.isFinite(Number(scale)) ? Number(scale) : 1;
    if (zoom < 0.55) return 0;
    if (zoom < 0.9) return 100;
    if (zoom < 1.5) return 250;
    return 400;
}

export function getWorldViewportBounds(transform, width, height, margin = TREE_LABEL_VIEWPORT_MARGIN) {
    const k = Math.max(0.0001, Number(transform?.k) || 1);
    const x = Number(transform?.x) || 0;
    const y = Number(transform?.y) || 0;
    const viewportWidth = Math.max(0, Number(width) || 0);
    const viewportHeight = Math.max(0, Number(height) || 0);
    const screenMargin = Math.max(0, Number(margin) || 0);

    return {
        minX: (-x - screenMargin) / k,
        maxX: (viewportWidth - x + screenMargin) / k,
        minY: (-y - screenMargin) / k,
        maxY: (viewportHeight - y + screenMargin) / k,
    };
}

export function isPointInsideBounds(point, bounds) {
    const x = Number(point?.x);
    const y = Number(point?.y);
    return Number.isFinite(x)
        && Number.isFinite(y)
        && x >= bounds.minX
        && x <= bounds.maxX
        && y >= bounds.minY
        && y <= bounds.maxY;
}

export function selectViewportLabels({
    nodes = [],
    transform = { x: 0, y: 0, k: 1 },
    width = 0,
    height = 0,
    margin = TREE_LABEL_VIEWPORT_MARGIN,
    forcedIds = new Set(),
    budget = getTreeLabelBudget(transform?.k),
    priorityForNode = () => 0,
} = {}) {
    const forced = forcedIds instanceof Set ? forcedIds : new Set(forcedIds || []);
    const bounds = getWorldViewportBounds(transform, width, height, margin);
    const forcedNodes = [];
    const candidates = [];

    for (const node of nodes) {
        const id = String(node?.id ?? '');
        if (forced.has(id)) {
            forcedNodes.push(node);
        } else if (isPointInsideBounds(node, bounds)) {
            candidates.push(node);
        }
    }

    const compare = (a, b) => {
        const priorityDifference = (Number(priorityForNode(b)) || 0) - (Number(priorityForNode(a)) || 0);
        if (priorityDifference) return priorityDifference;
        return String(a?.id ?? '').localeCompare(String(b?.id ?? ''), 'nl', { sensitivity: 'base' });
    };

    forcedNodes.sort(compare);
    candidates.sort(compare);
    return forcedNodes.concat(candidates.slice(0, Math.max(0, Number(budget) || 0)));
}

function resolveItemKey(item, index, itemKey) {
    if (typeof itemKey === 'function') return String(itemKey(item, index));
    if (typeof itemKey === 'string' && item && item[itemKey] != null) return String(item[itemKey]);
    return String(index);
}

export function buildVirtualOffsets(items = [], itemKey = 'id', measuredHeights = new Map(), estimatedRowHeight = 40) {
    const estimate = Math.max(1, Number(estimatedRowHeight) || 40);
    const offsets = new Array(items.length + 1);
    offsets[0] = 0;
    for (let index = 0; index < items.length; index += 1) {
        const key = resolveItemKey(items[index], index, itemKey);
        const measured = Number(measuredHeights?.get?.(key));
        offsets[index + 1] = offsets[index] + (Number.isFinite(measured) && measured > 0 ? measured : estimate);
    }
    return offsets;
}

export function findVirtualIndex(offsets, scrollOffset) {
    const itemCount = Math.max(0, (offsets?.length || 1) - 1);
    if (!itemCount) return 0;
    const target = Math.max(0, Number(scrollOffset) || 0);
    let low = 0;
    let high = itemCount - 1;

    while (low <= high) {
        const middle = Math.floor((low + high) / 2);
        if (offsets[middle + 1] <= target) low = middle + 1;
        else high = middle - 1;
    }
    return Math.min(itemCount - 1, Math.max(0, low));
}

export function getVirtualRange({ offsets = [0], scrollOffset = 0, viewportHeight = 0, overscan = 8 } = {}) {
    const itemCount = Math.max(0, offsets.length - 1);
    if (!itemCount) return { start: 0, end: 0 };
    const extra = Math.max(0, Math.floor(Number(overscan) || 0));
    const startVisible = findVirtualIndex(offsets, scrollOffset);
    const endVisible = findVirtualIndex(offsets, (Number(scrollOffset) || 0) + Math.max(0, Number(viewportHeight) || 0));
    return {
        start: Math.max(0, startVisible - extra),
        end: Math.min(itemCount, endVisible + extra + 1),
    };
}

export function getViewportWorldCenter(transform, width, height) {
    const k = Math.max(0.0001, Number(transform?.k) || 1);
    const x = Number(transform?.x) || 0;
    const y = Number(transform?.y) || 0;
    return {
        x: ((Math.max(0, Number(width) || 0) / 2) - x) / k,
        y: ((Math.max(0, Number(height) || 0) / 2) - y) / k,
    };
}

export function restoreTransformFromWorldCenter(worldCenter, scale, width, height) {
    const k = Math.max(0.0001, Number(scale) || 1);
    const centerX = Number(worldCenter?.x) || 0;
    const centerY = Number(worldCenter?.y) || 0;
    return {
        k,
        x: (Math.max(0, Number(width) || 0) / 2) - centerX * k,
        y: (Math.max(0, Number(height) || 0) / 2) - centerY * k,
    };
}

export function isCompatibleTreeViewState(state, privacyScope, view) {
    return Boolean(state)
        && state.schemaVersion === TREE_VIEW_STATE_SCHEMA_VERSION
        && state.data?.snapshotVersion === TREE_DATA_SNAPSHOT_VERSION
        && state.privacyScope === privacyScope
        && (!view || state.view?.type === view);
}
